import { useEffect, useState } from 'react';

export function SkeletonBlock({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded bg-neutral-200 ${className}`} />;
}

const MACHINE_ERROR_MESSAGES: Record<string, string> = {
  forbidden: 'You do not have access to this data.',
  internal_server_error: 'Something went wrong while loading this data.',
  not_found: 'The requested data was not found.',
  pod_forbidden: 'You do not have access to this pod-specific data.',
  unauthorized: 'Your session is no longer authorized.',
  validation_error: 'The request is invalid. Check the filters and try again.',
};

function humanizeErrorMessage(message: string): string {
  const trimmed = message.trim();
  if (!trimmed) return 'Something went wrong while loading this data.';
  const normalized = trimmed.toLowerCase();

  if (normalized.includes('service unavailable')) {
    return 'The service is temporarily unavailable. Try again in a moment.';
  }

  if (normalized.includes('internal server error')) {
    return 'Something went wrong while loading this data.';
  }

  if (normalized.includes('malformed error response')) {
    return 'The server returned an unexpected error response. Try again in a moment.';
  }

  if (normalized.includes('axiom_api_key') || normalized.includes('axiom api key')) {
    return 'Axiom log access is not configured for this environment.';
  }

  const machineCode = Object.keys(MACHINE_ERROR_MESSAGES).find((code) => trimmed.includes(code));
  if (machineCode) return MACHINE_ERROR_MESSAGES[machineCode];

  return trimmed;
}

export function LoadingState({ message, className = '' }: { message: string; className?: string }) {
  return (
    <div className={`space-y-3 px-4 py-6 ${className}`} aria-label={message}>
      <SkeletonBlock className='h-4 w-40 max-w-full' />
      <SkeletonBlock className='h-4 w-64 max-w-full' />
      <SkeletonBlock className='h-4 w-52 max-w-full' />
    </div>
  );
}

export function ErrorState({
  message,
  className = '',
  onRetry,
  title,
}: {
  message: string;
  className?: string;
  onRetry?: () => void | Promise<unknown>;
  title?: string;
}) {
  return <ErrorBanner message={message} className={className} onRetry={onRetry} title={title} />;
}

export function EmptyState({ message, className = '' }: { message: string; className?: string }) {
  return <div className={`px-4 py-6 text-sm text-neutral-600 ${className}`}>{message}</div>;
}

export function ErrorBanner({
  message,
  className = '',
  onRetry,
  retryInSeconds = 15,
  title = 'Could not load data',
}: {
  message: string;
  className?: string;
  onRetry?: () => void | Promise<unknown>;
  retryInSeconds?: number;
  title?: string;
}) {
  const [remainingSeconds, setRemainingSeconds] = useState(retryInSeconds);
  const displayMessage = humanizeErrorMessage(message);

  useEffect(() => {
    if (!onRetry || retryInSeconds <= 0) return;

    setRemainingSeconds(retryInSeconds);
    const intervalId = window.setInterval(() => {
      setRemainingSeconds((current) => Math.max(current - 1, 0));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [displayMessage, onRetry, retryInSeconds]);

  useEffect(() => {
    if (!onRetry || retryInSeconds <= 0 || remainingSeconds !== 0) return;

    onRetry();
    setRemainingSeconds(retryInSeconds);
  }, [onRetry, remainingSeconds, retryInSeconds]);

  return (
    <div
      className={`flex flex-col gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between ${className}`}
      role='alert'
    >
      <div className='min-w-0'>
        <div className='font-medium text-red-800'>{title}</div>
        <div className='mt-0.5 break-words'>{displayMessage}</div>
      </div>
      {onRetry && (
        <div className='flex shrink-0 items-center gap-3'>
          {retryInSeconds > 0 && <span className='text-xs text-red-600'>Retrying in {remainingSeconds}s</span>}
          <button
            className='rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100'
            onClick={() => {
              setRemainingSeconds(retryInSeconds);
              onRetry();
            }}
            type='button'
          >
            Retry now
          </button>
        </div>
      )}
    </div>
  );
}

export function LoadingCard({ message, className = '' }: { message: string; className?: string }) {
  return (
    <div className={`rounded-lg border border-neutral-200 bg-white px-4 py-6 ${className}`} aria-label={message}>
      <div className='space-y-3'>
        <SkeletonBlock className='h-4 w-44 max-w-full' />
        <SkeletonBlock className='h-4 w-72 max-w-full' />
        <SkeletonBlock className='h-4 w-56 max-w-full' />
      </div>
    </div>
  );
}

export function DataTableSkeleton({
  columns = 4,
  rows = 6,
  className = '',
  label = 'Loading table',
}: {
  columns?: number;
  rows?: number;
  className?: string;
  label?: string;
}) {
  return (
    <div className={`overflow-x-auto ${className}`} aria-label={label}>
      <table className='min-w-full divide-y divide-neutral-100 text-left text-sm'>
        <tbody className='divide-y divide-neutral-100 bg-white'>
          {Array.from({ length: rows }, (_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: columns }, (_, columnIndex) => (
                <td key={columnIndex} className='px-4 py-3'>
                  <div className={columnIndex === 0 ? 'flex items-center gap-3' : undefined}>
                    {columnIndex === 0 && <SkeletonBlock className='size-8 shrink-0 rounded-full' />}
                    <div className='min-w-0 flex-1 space-y-2'>
                      <SkeletonBlock className={columnIndex === 0 ? 'h-4 w-36' : 'h-4 w-24'} />
                      {columnIndex === 0 && <SkeletonBlock className='h-3 w-48 max-w-full' />}
                    </div>
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function LoadingOverlay({ label = 'Loading' }: { label?: string }) {
  return (
    <div
      className='absolute inset-x-0 bottom-[57px] top-[53px] flex items-center justify-center bg-white/70 backdrop-blur-[1px]'
      aria-label={label}
    >
      <div className='w-44 rounded-md border border-neutral-200 bg-white px-3 py-2 shadow-sm'>
        <SkeletonBlock className='h-4 w-full' />
      </div>
    </div>
  );
}

export function DetailPageSkeleton({
  fields = 9,
  label = 'Loading detail',
  showAvatar = false,
  tabs = 4,
}: {
  fields?: number;
  label?: string;
  showAvatar?: boolean;
  tabs?: number;
}) {
  return (
    <div className='space-y-6' aria-label={label}>
      <div className='rounded-lg border border-neutral-200 bg-white p-6'>
        <div className='flex items-start gap-4'>
          {showAvatar && <SkeletonBlock className='size-14 shrink-0 rounded-full' />}
          <div className='min-w-0 flex-1'>
            <SkeletonBlock className='mb-3 h-7 w-56 max-w-full' />
            <SkeletonBlock className='mb-5 h-4 w-72 max-w-full' />
            <div className='grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-3'>
              {Array.from({ length: fields }, (_, index) => (
                <SkeletonBlock key={index} className='h-4 w-40 max-w-full' />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className='overflow-hidden rounded-lg border border-neutral-200 bg-white'>
        <div className='flex gap-4 border-b border-neutral-200 px-4 py-3'>
          {Array.from({ length: tabs }, (_, index) => (
            <SkeletonBlock key={index} className='h-5 w-24' />
          ))}
        </div>
        <DataTableSkeleton columns={4} rows={4} />
      </div>
    </div>
  );
}
